import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import {
  Assessment,
  ShoppingCart,
  Description,
  PersonOutline,
  TrendingUp,
  GetApp,
  Refresh as RefreshIcon,
  PictureAsPdf,
  TableChart,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
    minHeight: 120,
  },
  cardIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: 24,
    fontWeight: "bold",
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textAlign: "center",
  },
  saleCard: {
    backgroundColor: "#e8f5e9",
    "& $cardIcon": { color: "#2e7d32" },
  },
  quoteCard: {
    backgroundColor: "#fff3e0",
    "& $cardIcon": { color: "#ef6c00" },
  },
  noSaleCard: {
    backgroundColor: "#ffebee",
    "& $cardIcon": { color: "#c62828" },
  },
  conversionCard: {
    backgroundColor: "#e3f2fd",
    "& $cardIcon": { color: "#1565c0" },
  },
  chipSale: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  chipQuote: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  chipNoSale: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
  refreshing: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  tableContainer: {
    maxHeight: 500,
    overflow: "auto",
  },
}));

const PdvReports = () => {
  const classes = useStyles();
  useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedStore, setSelectedStore] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedQueue, setSelectedQueue] = useState("all");
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [report, setReport] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        // Use PharmaFlow API
        const data = await pharmaApi.getStores();
        setStores(data || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchStores();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  // Fetch queues
  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchQueues();
  }, []);

  const fetchReport = useCallback(async (showLoadingIndicator = false) => {
    if (showLoadingIndicator) {
      setLoading(true);
    }
    setIsRefreshing(true);

    try {
      const params = {
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59`,
      };

      if (selectedStore && selectedStore !== "all") {
        params.storeId = selectedStore;
      }
      if (selectedUser && selectedUser !== "all") {
        params.userId = selectedUser;
      }
      if (selectedQueue && selectedQueue !== "all") {
        params.queueId = selectedQueue;
      }

      // Use Whaticket API (correlates tickets with orders/quotes from PharmaFlow)
      const { data } = await api.get("/pdv/reports/sales-attendance", { params });
      setReport(data);
      setLastRefresh(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [startDate, endDate, selectedStore, selectedUser, selectedQueue]);

  useEffect(() => {
    fetchReport(true);
  }, [fetchReport]);

  const handleExport = async (format) => {
    try {
      const params = {
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59`,
        format,
      };

      if (selectedStore && selectedStore !== "all") {
        params.storeId = selectedStore;
      }
      if (selectedUser && selectedUser !== "all") {
        params.userId = selectedUser;
      }
      if (selectedQueue && selectedQueue !== "all") {
        params.queueId = selectedQueue;
      }

      // Use Whaticket API for export
      const response = await api.get("/pdv/reports/sales-attendance/export", {
        params,
        responseType: 'blob'
      });
      const data = response.data;

      const extensions = {
        csv: "csv",
        excel: "xlsx",
        pdf: "pdf",
      };
      const mimeTypes = {
        csv: "text/csv",
        excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        pdf: "application/pdf",
      };

      const url = window.URL.createObjectURL(new Blob([data], { type: mimeTypes[format] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio-vendas-atendimentos-${startDate}-${endDate}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return "0.0%";
    return `${Number(value).toFixed(1)}%`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("pt-BR");
  };

  const getTypeChip = (type) => {
    switch (type) {
      case "sale":
        return <Chip label="Venda" size="small" className={classes.chipSale} />;
      case "quote":
        return <Chip label="Orçamento" size="small" className={classes.chipQuote} />;
      default:
        return <Chip label="Sem Venda" size="small" className={classes.chipNoSale} />;
    }
  };

  const StatCard = ({ icon: Icon, value, label, className }) => (
    <Card className={`${classes.card} ${className || ""}`}>
      <Icon className={classes.cardIcon} />
      <Typography className={classes.cardValue}>{value}</Typography>
      <Typography className={classes.cardLabel}>{label}</Typography>
    </Card>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>OtimizAPP Pharma - Relatórios de Vendas x Atendimentos</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title={`Última atualização: ${lastRefresh.toLocaleTimeString("pt-BR")}`}>
            <IconButton
              onClick={() => fetchReport(false)}
              disabled={isRefreshing}
              size="small"
            >
              <RefreshIcon className={isRefreshing ? classes.refreshing : ""} />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {/* Filtros */}
        <Box className={classes.filterContainer}>
          <TextField
            label="Data Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
          <TextField
            label="Data Fim"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Loja</InputLabel>
            <Select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              label="Loja"
            >
              <MenuItem value="all">Todas</MenuItem>
              {(stores || []).map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Atendente</InputLabel>
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="Atendente"
            >
              <MenuItem value="all">Todos</MenuItem>
              {(users || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Fila</InputLabel>
            <Select
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
              label="Fila"
            >
              <MenuItem value="all">Todas</MenuItem>
              {(queues || []).map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<TableChart />}
            onClick={() => handleExport("excel")}
            style={{ marginLeft: "auto" }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={() => handleExport("pdf")}
            color="secondary"
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => handleExport("csv")}
          >
            CSV
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Cards de Resumo */}
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={Assessment}
                  value={report.summary.totalAttendances}
                  label="Total de Atendimentos"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={ShoppingCart}
                  value={report.summary.attendancesWithSale}
                  label="Atendimentos com Venda"
                  className={classes.saleCard}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={Description}
                  value={report.summary.attendancesWithQuote}
                  label="Atendimentos com Orçamento"
                  className={classes.quoteCard}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={PersonOutline}
                  value={report.summary.attendancesWithoutSaleOrQuote}
                  label="Atendimentos sem Venda/Orçamento"
                  className={classes.noSaleCard}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={TrendingUp}
                  value={formatPercent(report.summary.conversionRate)}
                  label="Taxa de Conversão"
                  className={classes.conversionCard}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={TrendingUp}
                  value={formatPercent(report.summary.quoteRate)}
                  label="Taxa de Orçamentos"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={ShoppingCart}
                  value={formatCurrency(report.summary.totalSalesValue)}
                  label="Total em Vendas"
                  className={classes.saleCard}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={ShoppingCart}
                  value={formatCurrency(report.summary.averageTicket)}
                  label="Ticket Médio"
                />
              </Grid>
            </Grid>

            {/* Tabs para diferentes visualizações */}
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Detalhes" />
              <Tab label="Por Atendente" />
              <Tab label="Por Fila" />
              <Tab label="Por Dia" />
            </Tabs>

            <Box className={classes.tabContent}>
              {tabValue === 0 && (
                <Box className={classes.tableContainer}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Contato</TableCell>
                        <TableCell>Atendente</TableCell>
                        <TableCell>Fila</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Pedido/Orçamento</TableCell>
                        <TableCell align="right">Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.details || []).map((detail) => (
                        <TableRow key={detail.ticketId}>
                          <TableCell>#{detail.ticketId}</TableCell>
                          <TableCell>
                            <div>{detail.contactName}</div>
                            <Typography variant="caption" color="textSecondary">
                              {detail.contactPhone}
                            </Typography>
                          </TableCell>
                          <TableCell>{detail.attendantName}</TableCell>
                          <TableCell>{detail.queueName}</TableCell>
                          <TableCell>{formatDate(detail.ticketCreatedAt)}</TableCell>
                          <TableCell>{getTypeChip(detail.type)}</TableCell>
                          <TableCell>
                            {detail.orderNumber || detail.quoteNumber || "-"}
                          </TableCell>
                          <TableCell align="right">
                            {detail.orderTotal
                              ? formatCurrency(detail.orderTotal)
                              : detail.quoteTotal
                              ? formatCurrency(detail.quoteTotal)
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {tabValue === 1 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Atendente</TableCell>
                      <TableCell align="right">Total Atendimentos</TableCell>
                      <TableCell align="right">Vendas</TableCell>
                      <TableCell align="right">Orçamentos</TableCell>
                      <TableCell align="right">Sem Venda</TableCell>
                      <TableCell align="right">Taxa Conversão</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.summary?.byAttendant || []).map((att) => (
                      <TableRow key={att.attendantId}>
                        <TableCell>{att.attendantName}</TableCell>
                        <TableCell align="right">{att.totalAttendances}</TableCell>
                        <TableCell align="right">{att.sales}</TableCell>
                        <TableCell align="right">{att.quotes}</TableCell>
                        <TableCell align="right">{att.noSale}</TableCell>
                        <TableCell align="right">{formatPercent(att.conversionRate)}</TableCell>
                        <TableCell align="right">{formatCurrency(att.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {tabValue === 2 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fila</TableCell>
                      <TableCell align="right">Total Atendimentos</TableCell>
                      <TableCell align="right">Vendas</TableCell>
                      <TableCell align="right">Orçamentos</TableCell>
                      <TableCell align="right">Sem Venda</TableCell>
                      <TableCell align="right">Taxa Conversão</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.summary?.byQueue || []).map((queue) => (
                      <TableRow key={queue.queueId}>
                        <TableCell>{queue.queueName}</TableCell>
                        <TableCell align="right">{queue.totalAttendances}</TableCell>
                        <TableCell align="right">{queue.sales}</TableCell>
                        <TableCell align="right">{queue.quotes}</TableCell>
                        <TableCell align="right">{queue.noSale}</TableCell>
                        <TableCell align="right">{formatPercent(queue.conversionRate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {tabValue === 3 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Total Atendimentos</TableCell>
                      <TableCell align="right">Vendas</TableCell>
                      <TableCell align="right">Orçamentos</TableCell>
                      <TableCell align="right">Sem Venda</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.summary?.byDay || []).map((day) => (
                      <TableRow key={day.date}>
                        <TableCell>
                          {new Date(day.date + "T12:00:00").toLocaleDateString("pt-BR")}
                        </TableCell>
                        <TableCell align="right">{day.totalAttendances}</TableCell>
                        <TableCell align="right">{day.sales}</TableCell>
                        <TableCell align="right">{day.quotes}</TableCell>
                        <TableCell align="right">{day.noSale}</TableCell>
                        <TableCell align="right">{formatCurrency(day.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        ) : (
          <Typography>Nenhum dado disponível</Typography>
        )}
      </Paper>
    </MainContainer>
  );
};

export default PdvReports;
